import { useState, useRef, useEffect, FC } from 'react';
import { AudioOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { App, Button, Flex } from 'antd';

import {
  Overlay,
  RecordButton,
  RecordMinuteIcon,
  RecordPlayer
} from './styled';

const MAX_DURATION = 60;
const MIME_TYPES_TO_CHECK = [
  'audio/webm',
  'audio/m4a',
  'audio/mp3',
  'audio/mp4',
  'audio/ogg',
  'audio/wav'
];

const getMimeType = () => {
  const type = MIME_TYPES_TO_CHECK.find(item =>
    MediaRecorder.isTypeSupported(item)
  );

  if (type) {
    return type;
  }

  throw new Error('Mime type is not supported');
};

interface IProps {
  disabled?: boolean;
  onCancel?(): void;
  onFinish(url: string): void;
  resetCancelFlag?: () => void;
}

const VoiceRecorder: FC<IProps> = ({
  onFinish,
  onCancel,
  resetCancelFlag,
  disabled
}) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [isOpenOverlay, setIsOpenOverlay] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioChunks = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const startRecording = async () => {
    try {
      mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: true
      });

      const mimeType = getMimeType();

      console.log('mimeType', mimeType);

      const mediaRecorder = new MediaRecorder(mediaStreamRef.current, {
        mimeType
      });

      mediaRecorder.ondataavailable = event => {
        audioChunks.current.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunks.current, { type: mimeType });
        const audioURL = URL.createObjectURL(audioBlob);
        onFinish(audioURL);
        audioChunks.current = [];
      };

      mediaRecorder.start();
      startTimer();
      mediaRecorderRef.current = mediaRecorder;
      setIsOpenOverlay(true);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log('mediaStreamRef.current', mediaStreamRef.current);
      console.log('err', error);
      setTimeout(() => {
        mediaStreamRef.current?.getTracks().forEach(track => track.stop());
      }, 50);
      const errorMessage =
        error?.message || t('validation.something_went_wrong');

      message.error(errorMessage);
    }
  };

  const cancelRecording = () => {
    stopRecording();
    onCancel?.();
    clearAll();
  };

  const handleDone = () => {
    stopRecording();
    resetCancelFlag?.();
    clearAll();
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setTimeout(() => {
      mediaStreamRef.current?.getTracks().forEach(track => track.stop());
    }, 50);
    clearTimer();
  };

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setElapsedTime(prev => {
        if (prev + 1 >= MAX_DURATION) {
          clearTimer();
        }

        return prev + 1;
      });
    }, 1000);
  };

  const clearTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const clearAll = () => {
    setIsOpenOverlay(false);
    setElapsedTime(0);
    audioChunks.current = [];
    mediaRecorderRef.current = null;
  };

  useEffect(() => {
    return () => {
      clearTimer();
    };
  }, []);

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;

    return `${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`;
  };

  return (
    <>
      <Overlay style={{ display: isOpenOverlay ? 'flex' : 'none' }}>
        <RecordPlayer>
          <Button danger onClick={cancelRecording}>
            {t('common.cancel')}
          </Button>
          <Flex align="center" gap={8}>
            <RecordMinuteIcon />
            <div style={{ fontSize: 18 }}>
              {formatTime(elapsedTime)}/{formatTime(MAX_DURATION)}
            </div>
          </Flex>
          <Button type="primary" onClick={handleDone}>
            {t('reports.done')}
          </Button>
        </RecordPlayer>
      </Overlay>
      <RecordButton
        onClick={startRecording}
        type="text"
        disabled={disabled}
        icon={<AudioOutlined style={{ color: '#FF4C4E', fontSize: 18 }} />}
      >
        {t('reports.record')}
      </RecordButton>
    </>
  );
};

export default VoiceRecorder;
